const SWIPER_ROOT_SELECTORS = ['.featured-books-swiper', '.reviews-swiper'];
let pageSwiperInitializationStarted = false;

export function waitForSwiper(callback, maxAttempts = 20, attempt = 0) {
    if (typeof Swiper !== 'undefined') {
        callback();
        return true;
    }
    if (attempt >= maxAttempts) {
        console.error('Swiper library failed to load after multiple attempts');
        return false;
    }
    setTimeout(() => waitForSwiper(callback, maxAttempts, attempt + 1), 200);
    return true;
}

export function initPageSwipers(maxAttempts = 20) {
    if (pageSwiperInitializationStarted
        || !SWIPER_ROOT_SELECTORS.some(selector => document.querySelector(selector))) {
        return false;
    }
    // Home's later Vue mount replaces the Featured Books root. Wait for that
    // mount signal instead of initializing a root that will be detached.
    if (document.querySelector('#featured-books-section') && !window.foxyHomepageMounted) {
        return false;
    }
    pageSwiperInitializationStarted = true;
    const initialize = () => waitForSwiper(() => {
        // Resolve current roots after Vue mounting, not detached pre-mount nodes.
        initFeaturedBooksSwiper();
        initReviewsSwiper();
    }, maxAttempts);
    if (typeof window.Vue?.nextTick === 'function') {
        window.Vue.nextTick(initialize);
    } else {
        initialize();
    }
    return true;
}

/**
 * Swiper initialization for reviews slider
 */

function setReviewControlVisibility(control, isVisible) {
    const controls = Array.isArray(control) ? control : [control];

    controls.filter(Boolean).forEach((element) => {
        element.hidden = !isVisible;
        element.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        element.tabIndex = isVisible ? 0 : -1;
    });
}

export function syncReviewsNavigation(swiper) {
    if (!swiper?.navigation) {
        return;
    }

    const navigationUnavailable = swiper.isLocked || (swiper.isBeginning && swiper.isEnd);

    setReviewControlVisibility(
        swiper.navigation.prevEl,
        !navigationUnavailable && !swiper.isBeginning,
    );
    setReviewControlVisibility(
        swiper.navigation.nextEl,
        !navigationUnavailable && !swiper.isEnd,
    );
}

export function initReviewsSwiper() {
    const swiperContainer = document.querySelector('.reviews-swiper');
    if (!swiperContainer || typeof Swiper === 'undefined') return null;

    // Check if swiper is already initialized
    if (swiperContainer.swiper) {
        console.log('Reviews Swiper already initialized');
        return swiperContainer.swiper;
    }

    try {
        const reviewsSwiper = new Swiper('.reviews-swiper', {
            slidesPerView: 1,
            spaceBetween: 16,
            loop: false,
            watchOverflow: true,
            // Touch/Swipe support - основные настройки для свайпа
            touchEventsTarget: 'container',
            allowTouchMove: true,
            touchRatio: 1,
            touchAngle: 45,
            grabCursor: true,
            // Настройки для более плавного свайпа
            resistance: true,
            resistanceRatio: 0.85,
            // Navigation - кнопки назад/вперед
            navigation: {
                nextEl: '.reviews-slider-next',
                prevEl: '.reviews-slider-prev',
                disabledClass: 'swiper-button-disabled',
                hideOnClick: false,
            },
            // Keyboard control - стрелки на клавиатуре
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            },
            // Mouse wheel control (опционально)
            mousewheel: {
                forceToAxis: true,
                sensitivity: 1,
                releaseOnEdges: false,
            },
            // Адаптивные настройки - минимум 3 блока на десктопе
            breakpoints: {
                640: {
                    slidesPerView: 2,
                    spaceBetween: 16,
                },
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 20,
                },
                1280: {
                    slidesPerView: 3,
                    spaceBetween: 24,
                },
            },
            // События для отладки
            on: {
                init: function() {
                    syncReviewsNavigation(this);
                    console.log('Reviews Swiper initialized successfully');
                },
                slideChange: function() {
                    syncReviewsNavigation(this);
                },
                resize: function() {
                    syncReviewsNavigation(this);
                },
                breakpoint: function() {
                    syncReviewsNavigation(this);
                },
                update: function() {
                    syncReviewsNavigation(this);
                },
                lock: function() {
                    syncReviewsNavigation(this);
                },
                unlock: function() {
                    syncReviewsNavigation(this);
                },
            },
        });

        console.log('Reviews Swiper created:', reviewsSwiper);
        return reviewsSwiper;
    } catch (error) {
        console.error('Error initializing Reviews Swiper:', error);
        return null;
    }
}

/**
 * Swiper initialization for featured books slider
 */

export function initFeaturedBooksSwiper() {
    const swiperContainer = document.querySelector('.featured-books-swiper');
    if (!swiperContainer || typeof Swiper === 'undefined') return null;

    // Check if swiper is already initialized
    if (swiperContainer.swiper) {
        console.log('Featured Books Swiper already initialized');
        return swiperContainer.swiper;
    }

    try {
        const featuredBooksSwiper = new Swiper('.featured-books-swiper', {
            slidesPerView: 1,
            spaceBetween: 16,
            loop: false,
            // Touch/Swipe support - основные настройки для свайпа
            touchEventsTarget: 'container',
            allowTouchMove: true,
            touchRatio: 1,
            touchAngle: 45,
            grabCursor: true,
            // Настройки для более плавного свайпа
            resistance: true,
            resistanceRatio: 0.85,
            // Navigation - кнопки назад/вперед
            navigation: {
                nextEl: '.featured-books-next',
                prevEl: '.featured-books-prev',
                disabledClass: 'swiper-button-disabled',
                hideOnClick: false,
            },
            // Keyboard control - стрелки на клавиатуре
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            },
            // Mouse wheel control (опционально)
            mousewheel: {
                forceToAxis: true,
                sensitivity: 1,
                releaseOnEdges: false,
            },
            // Адаптивные настройки
            breakpoints: {
                640: {
                    slidesPerView: 2,
                    spaceBetween: 16,
                },
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 20,
                },
                1280: {
                    slidesPerView: 3,
                    spaceBetween: 24,
                },
            },
            // События для отладки
            on: {
                init: function() {
                    console.log('Featured Books Swiper initialized successfully');
                },
                slideChange: function() {
                    // Обновление состояния кнопок происходит автоматически
                },
            },
        });

        console.log('Featured Books Swiper created:', featuredBooksSwiper);
        return featuredBooksSwiper;
    } catch (error) {
        console.error('Error initializing Featured Books Swiper:', error);
        return null;
    }
}
